import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { FlashList } from '@shopify/flash-list';
import { useAppSelector, useAppStore } from 'store/hooks';
import { AppView, Indicator, withAnimatedNavigationHeader } from 'design-system';
import { SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { muteAll, useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import EmptyState from 'features/invest/components/EmptyState';
import { getAutosaveActivities } from 'features/pots/actions';
import AutosaveActivityRowItem from 'features/pots/components/AutosaveActivityItem';
import { selectAutosaveActivitiesInitialLoading, selectAutosaveActivitySections, } from 'features/pots/reducers/selectors';
import { extractKeyFromKey } from 'utils/flatListHelpers';
import { isWeb } from '../../../../constants';
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const renderItem = ({ item }) => <AutosaveActivityRowItem activity={item}/>;
const AutosaveActivities = withAnimatedNavigationHeader(({ navigation, scrollHandler }) => {
    const store = useAppStore();
    const shouldLoadSecondPage = useRef(false);
    const [, , , fetch] = useFetchOld(muteAll);
    const loading = useAppSelector((state) => state.unpersistedPotsReducer.fetchingAutosaveActivities);
    const initialLoading = useAppSelector(selectAutosaveActivitiesInitialLoading);
    const flashListData = useAppSelector(selectAutosaveActivitySections);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.AUTOSAVE_ACTIVITY}/>,
        });
    }, []);
    useEffect(() => {
        if (!initialLoading) {
            fetch(getAutosaveActivities({
                page: 1,
                perPage: 30,
            }));
        }
        // Runs only once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const loadMoreData = useCallback(() => {
        const token = store.getState().unpersistedPotsReducer.autosaveActivitiesPaginationToken;
        if (token) {
            fetch(getAutosaveActivities({
                token,
            }));
        }
    }, []);
    useEffect(() => {
        if (!loading && shouldLoadSecondPage.current) {
            shouldLoadSecondPage.current = false;
            loadMoreData();
        }
    }, [loadMoreData, loading]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyles = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    const onLoadNextPage = useCallback(() => {
        if (loading) {
            shouldLoadSecondPage.current = true;
        }
        else {
            loadMoreData();
        }
    }, [loadMoreData, loading]);
    const content = useMemo(() => {
        if (initialLoading && !flashListData?.length) {
            return <Indicator dark/>;
        }
        if (flashListData?.length === 0) {
            return (<View style={paddingHorizontalStyle}>
            <EmptyState activity subtitle="Your autosave activities will show here"/>
          </View>);
        }
        return (<AnimatedFlashList data={flashListData} renderItem={renderItem} scrollEventThrottle={16} onScroll={scrollHandler} onEndReachedThreshold={0.1} onEndReached={onLoadNextPage} keyExtractor={extractKeyFromKey('id')} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} showsVerticalScrollIndicator contentContainerStyle={contentContainerStyles}/>);
    }, [initialLoading, flashListData, scrollHandler, onLoadNextPage, contentContainerStyles, paddingHorizontalStyle]);
    return <AppView withFrame={false}>{content}</AppView>;
}, ({ colors }) => ({
    title: 'Activity',
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export default AutosaveActivities;
